import { useState, useEffect } from 'react'
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import Button from '../components/Button'
import LoadingSpinner from '../components/LoadingSpinner'
import api from '../services/api'
import { getToken, getUser } from '../utils/userData'
import {
  getMediaUrl,
  isExternalVideoUrl,
  getVideoEmbedUrl,
  getYouTubeThumbnail,
  getMediaDisplayName,
} from '../utils/mediaHelpers'
import {
  initMetaPixel,
  trackAddToCart,
  trackInitiateCheckout,
  trackPurchase,
  trackViewContent,
} from '../utils/metaPixel'
import { trackEvent } from '../utils/mixpanel'
import {
  needsExtendedForm2,
  needsFamilyIntakeForm,
  needsUnmaskingHalfdayApr2026Form,
  needsAWayToMoveForm,
  getAvailableAWayToMoveSlotOptions,
  UNMASKING_SESSION_OPTIONS,
  UNMASKING_GENDER_OPTIONS,
} from '../utils/eventRegistrationFormTypes'
import ExtendedForm2PaymentSection from '../components/events/ExtendedForm2PaymentSection'
import AWayToMoveSlotPicker from '../components/events/AWayToMoveSlotPicker'
import { loadRazorpayScript, openRazorpayCheckout } from '../utils/razorpay'
import { parseFee } from '../utils/parseFee'

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000)
    return () => clearTimeout(timer)
  }, [onClose])

  const bgColor = type === 'success' ? 'bg-green-600' : type === 'error' ? 'bg-red-600' : 'bg-primary-600'
  const icon = type === 'success' ? '✓' : type === 'error' ? '✕' : 'ℹ'

  return (
    <motion.div
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -50 }}
      className={`fixed top-4 left-1/2 -translate-x-1/2 z-[100] ${bgColor} text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-3`}
    >
      <span className="text-lg font-bold">{icon}</span>
      <span className="font-medium">{message}</span>
      <button onClick={onClose} className="ml-2 hover:bg-white/20 rounded-full p-1">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </motion.div>
  )
}

const ConfirmModal = ({ isOpen, title, message, onConfirm, onCancel, confirmText = 'Confirm', cancelText = 'Cancel' }) => {
  if (!isOpen) return null

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600 mb-6">{message}</p>
        <div className="flex gap-3 justify-end">
          <Button variant="secondary" onClick={onCancel}>{cancelText}</Button>
          <Button variant="primary" onClick={onConfirm}>{confirmText}</Button>
        </div>
      </motion.div>
    </motion.div>
  )
}

const MediaThumbnails = ({ media, selectedIndex, onSelect }) => {
  if (!media || media.length === 0) return null

  return (
    <div className="flex gap-1.5 overflow-x-auto pb-2 mb-4">
      {media.map((item, index) => {
        const isOriginalCover = item.isOriginalCover
        const mediaUrl = isOriginalCover
          ? (item.url.startsWith('http') ? item.url : `${import.meta.env.VITE_API_URL || ''}${item.url}`)
          : getMediaUrl(item.url)
        const isSelected = selectedIndex === index
        const isExternal = isExternalVideoUrl(item.url)
        const thumbnailUrl = isExternal ? getYouTubeThumbnail(item.url) : null

        if (item.type === 'pdf') {
          return (
            <a
              key={item.id}
              href={mediaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 w-10 h-10 rounded overflow-hidden bg-red-50 flex items-center justify-center hover:bg-red-100 transition-colors border border-gray-200"
              title={getMediaDisplayName(item)}
            >
              <span className="text-sm">📄</span>
            </a>
          )
        }

        return (
          <button
            key={item.id}
            onClick={() => onSelect(index)}
            className={`flex-shrink-0 w-10 h-10 rounded overflow-hidden bg-gray-100 transition-all ${
              isSelected
                ? 'ring-2 ring-primary-500 ring-offset-1'
                : 'hover:opacity-80 border border-gray-200'
            }`}
            title={isOriginalCover ? 'Original cover' : (item.caption || '')}
          >
            {item.type === 'image' && (
              <img
                src={mediaUrl}
                alt={item.caption || 'Thumbnail'}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = '/uploads/events/default-event.jpg' }}
              />
            )}
            {item.type === 'video' && (
              <div className="w-full h-full relative">
                {thumbnailUrl ? (
                  <img src={thumbnailUrl} alt="Video" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                    <span className="text-xs">🎬</span>
                  </div>
                )}
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              </div>
            )}
          </button>
        )
      })}
    </div>
  )
}

const getFormatBadgeColor = (format) => {
  switch (format.toLowerCase()) {
    case 'online': return 'bg-blue-100 text-blue-800'
    case 'in-person': return 'bg-green-100 text-green-800'
    case 'hybrid': return 'bg-purple-100 text-purple-800'
    default: return 'bg-gray-100 text-gray-800'
  }
}

const formatDate = (dateStr) => {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-IN', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

const formatTime = (dateStr) => {
  const date = new Date(dateStr)
  return date.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatDateRange = (startDateStr, endDateStr) => {
  const start = new Date(startDateStr)
  if (!endDateStr) return formatDate(startDateStr)

  const end = new Date(endDateStr)
  if (start.toDateString() === end.toDateString()) {
    return formatDate(startDateStr)
  }

  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()
  if (sameMonth) {
    const startPart = start.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric' })
    const endPart = end.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })
    return `${startPart} - ${endPart}`
  }
  const startPart = start.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'long' })
  const endPart = end.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })
  return `${startPart} - ${endPart}`
}

const getEventImage = (event) => {
  if (event.image) {
    if (event.image.startsWith('http')) return event.image
    return `${import.meta.env.VITE_API_URL || ''}${event.image}`
  }
  if (event.media && event.media.length > 0) {
    const firstImageMedia = event.media.find(m => m.type === 'image')
    if (firstImageMedia) return getMediaUrl(firstImageMedia.url)
  }
  return `${import.meta.env.VITE_API_URL}/uploads/events/default-event.jpg`
}

const getLocationText = (event) => {
  if (event.space) {
    return event.space.name + (event.space.city ? `, ${event.space.city}` : '')
  }
  return event.location || (event.format === 'online' ? 'Online' : 'Location TBA')
}

const getFacilitatorNames = (event) => {
  const names = []
  if (event.facilitators?.length > 0) names.push(...event.facilitators.map(f => f.name))
  if (event.externalFacilitators?.length > 0) names.push(...event.externalFacilitators)
  return names
}

const renderFacilitatorLinks = (event) => {
  const elements = []

  if (event.facilitators?.length > 0) {
    event.facilitators.forEach((f, index) => {
      if (index > 0 || elements.length > 0) elements.push(<span key={`sep-${index}`}>, </span>)
      if (f.slug) {
        elements.push(
          <Link
            key={`fac-${f.id || index}`}
            to={`/facilitator/${f.slug}`}
            className="text-primary-600 underline font-semibold hover:text-primary-700 transition-colors"
          >
            {f.name}
          </Link>
        )
      } else {
        elements.push(<span key={`fac-${f.id || index}`}>{f.name}</span>)
      }
    })
  }

  if (event.externalFacilitators?.length > 0) {
    event.externalFacilitators.forEach((name, index) => {
      if (elements.length > 0) elements.push(<span key={`ext-sep-${index}`}>, </span>)
      elements.push(<span key={`ext-${index}`}>{name}</span>)
    })
  }

  return elements
}

const COUNTRY_CODES = [
  { code: '+91', label: '🇮🇳 +91' },
  { code: '+1', label: '🇺🇸 +1' },
  { code: '+44', label: '🇬🇧 +44' },
  { code: '+971', label: '🇦🇪 +971' },
  { code: '+65', label: '🇸🇬 +65' },
  { code: '+61', label: '🇦🇺 +61' },
  { code: '+49', label: '🇩🇪 +49' },
  { code: '+33', label: '🇫🇷 +33' },
  { code: '+81', label: '🇯🇵 +81' },
  { code: '+86', label: '🇨🇳 +86' },
  { code: '+966', label: '🇸🇦 +966' },
  { code: '+974', label: '🇶🇦 +974' },
  { code: '+60', label: '🇲🇾 +60' },
  { code: '+62', label: '🇮🇩 +62' },
  { code: '+27', label: '🇿🇦 +27' },
  { code: '+234', label: '🇳🇬 +234' },
  { code: '+254', label: '🇰🇪 +254' },
]

const PRIMARY_GOAL_OPTIONS = [
  'I lead a high stress life and think fixing my breathing can help',
  'I struggle with sleep and think that breathing better can help',
  'I suffer from anxiety and depression and would like to know how breathing better can help',
  'I want to understand what breathing right every day looks like so I fix my breathing',
  "I'm just curious",
]

const HEARD_ABOUT_OPTIONS = [
  'Social Media (LinkedIn, Facebook, etc.)',
  'From ads',
  'From a friend',
  'Whatsapp Group',
]

const needsExtendedForm = (event) => event?.formType === 'extended-workshop'
const EXTENDED_FORM_PAYMENT_TEXT = 'Please transfer Rs. 99 to the following QR code'

const EventDetail = () => {
  const { slug } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const [event, setEvent] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [registering, setRegistering] = useState(false)
  const [isRegistered, setIsRegistered] = useState(false)
  const [toast, setToast] = useState(null)
  const [confirmModal, setConfirmModal] = useState(false)
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0)
  const [guestName, setGuestName] = useState('')
  const [guestEmail, setGuestEmail] = useState('')
  const [guestPhone, setGuestPhone] = useState('')
  const [guestRegistered, setGuestRegistered] = useState(false)

  // Extended form state
  const [guestCountryCode, setGuestCountryCode] = useState('+91')
  const [guestJobTitle, setGuestJobTitle] = useState('')
  const [guestOrganization, setGuestOrganization] = useState('')
  const [guestPrimaryGoals, setGuestPrimaryGoals] = useState([])
  const [guestOtherGoal, setGuestOtherGoal] = useState('')
  const [guestHeardAbout, setGuestHeardAbout] = useState('')
  const [guestOtherHeardAbout, setGuestOtherHeardAbout] = useState('')
  const [guestTransactionId, setGuestTransactionId] = useState('')

  const [guestChildGender, setGuestChildGender] = useState('')
  const [guestChildGenderOther, setGuestChildGenderOther] = useState('')
  const [guestChildAge, setGuestChildAge] = useState('')
  const [guestAboutYourself, setGuestAboutYourself] = useState('')
  const [guestSessionHope, setGuestSessionHope] = useState('')
  const [guestSharedQuestions, setGuestSharedQuestions] = useState('')

  const [guestAge, setGuestAge] = useState('')
  const [guestGender, setGuestGender] = useState('')
  const [guestGenderOther, setGuestGenderOther] = useState('')
  const [guestCompany, setGuestCompany] = useState('')
  const [guestSessionSelection, setGuestSessionSelection] = useState('')
  const [guestFacilitatorQueries, setGuestFacilitatorQueries] = useState('')
  const [guestSlotSelection, setGuestSlotSelection] = useState('')

  const isLoggedIn = !!getToken()
  const currentUser = getUser()
  const currentUserName = currentUser?.name
  const currentUserEmail = currentUser?.email
  const currentUserPhone = currentUser?.phone

  useEffect(() => {
    initMetaPixel()
    trackEvent('Page Viewed', { page: 'Event Detail', slug })
  }, [slug])

  useEffect(() => {
    setGuestChildGender('')
    setGuestChildGenderOther('')
    setGuestChildAge('')
    setGuestAboutYourself('')
    setGuestSessionHope('')
    setGuestSharedQuestions('')
    setGuestAge('')
    setGuestGender('')
    setGuestGenderOther('')
    setGuestCompany('')
    setGuestSessionSelection('')
    setGuestFacilitatorQueries('')
    setGuestSlotSelection('')
  }, [slug])

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        setLoading(true)
        setError(null)
        const response = await api.get(`/api/events/${slug}`)
        const data = response.data.data
        setEvent(data)
        setIsRegistered(data.isRegistered || false)
        trackViewContent(data)
      } catch (err) {
        if (err.response?.status === 404) {
          setError('Event not found.')
        } else {
          setError('Failed to load event. Please try again later.')
        }
      } finally {
        setLoading(false)
      }
    }
    fetchEvent()
  }, [slug])

  useEffect(() => {
    if (event && isLoggedIn) {
      if (currentUserName) setGuestName(currentUserName)
      if (currentUserEmail) setGuestEmail(currentUserEmail)
      if (currentUserPhone) setGuestPhone(currentUserPhone)
    }
  }, [event, isLoggedIn, currentUserName, currentUserEmail, currentUserPhone])

  // Pre-fill guest fields from Events-page handoff (name/email/phone carried via router state)
  useEffect(() => {
    if (!isLoggedIn && location.state?.prefill) {
      const { name, email, phone } = location.state.prefill
      if (name) setGuestName(name)
      if (email) setGuestEmail(email)
      if (phone) setGuestPhone(phone)
    }
  }, [isLoggedIn, location.state])

  const showToast = (message, type = 'info') => setToast({ message, type })
  const hideToast = () => setToast(null)

  const buildFamilyIntakeFormData = () => ({
    childGender:
      guestChildGender === 'Other'
        ? `Other: ${guestChildGenderOther.trim()}`
        : guestChildGender.trim(),
    childAge: guestChildAge.trim(),
    aboutYourself: guestAboutYourself.trim(),
    sessionHope: guestSessionHope.trim(),
    questions: guestSharedQuestions.trim(),
  })

  const validateFamilyIntakeForSubmit = () => {
    if (!guestPhone.trim()) {
      showToast('Please enter your phone number', 'error')
      return false
    }
    if (guestChildGender !== 'Male' && guestChildGender !== 'Other') {
      showToast("Please select your child's gender", 'error')
      return false
    }
    if (guestChildGender === 'Other' && !guestChildGenderOther.trim()) {
      showToast('Please specify for Other under child\'s gender', 'error')
      return false
    }
    if (!guestChildAge.trim()) {
      showToast("Please enter your child's age", 'error')
      return false
    }
    if (!guestAboutYourself.trim()) {
      showToast('Please tell us a little about yourself', 'error')
      return false
    }
    if (!guestSessionHope.trim()) {
      showToast('Please share what you hope to get out of this session', 'error')
      return false
    }
    if (!guestSharedQuestions.trim()) {
      showToast('Please complete the questions field (or write "None")', 'error')
      return false
    }
    return true
  }

  const buildUnmaskingHalfdayFormData = () => ({
    age: guestAge.trim(),
    gender:
      guestGender === 'Other'
        ? `Other: ${guestGenderOther.trim()}`
        : guestGender.trim(),
    company: guestCompany.trim(),
    sessionSelection: guestSessionSelection.trim(),
    transactionId: guestTransactionId.trim(),
    ...(guestFacilitatorQueries.trim()
      ? { facilitatorQueries: guestFacilitatorQueries.trim() }
      : {}),
  })

  const buildAWayToMoveFormData = () => ({
    slotSelection: guestSlotSelection.trim(),
  })

  const validateAWayToMoveForSubmit = () => {
    if (getAvailableAWayToMoveSlotOptions().length === 0) {
      showToast('No trial slots are available right now', 'error')
      return false
    }
    if (!guestSlotSelection.trim()) {
      showToast('Please select a trial slot', 'error')
      return false
    }
    return true
  }

  const validateUnmaskingHalfdayForSubmit = () => {
    if (!guestAge.trim()) {
      showToast('Please enter your age', 'error')
      return false
    }
    if (!guestGender) {
      showToast('Please select your gender', 'error')
      return false
    }
    if (guestGender === 'Other' && !guestGenderOther.trim()) {
      showToast('Please specify your gender', 'error')
      return false
    }
    if (!guestCompany.trim()) {
      showToast('Please enter your company', 'error')
      return false
    }
    if (!guestSessionSelection.trim()) {
      showToast('Please select a session option', 'error')
      return false
    }
    if (!event.paymentGatewayEnabled && !guestTransactionId.trim()) {
      showToast('Please enter your payment transaction ID', 'error')
      return false
    }
    return true
  }

  const buildExtendedFormData = () => {
    const goals = [...guestPrimaryGoals]
    if (guestPrimaryGoals.includes('Other') && guestOtherGoal.trim()) {
      goals[goals.indexOf('Other')] = `Other: ${guestOtherGoal.trim()}`
    } else {
      const otherIdx = goals.indexOf('Other')
      if (otherIdx !== -1) goals.splice(otherIdx, 1)
    }
    return {
      jobTitle: guestJobTitle.trim() || undefined,
      organization: guestOrganization.trim() || undefined,
      primaryGoals: goals.length > 0 ? goals : undefined,
      heardAbout: guestHeardAbout === 'Other'
        ? `Other: ${guestOtherHeardAbout.trim()}`
        : guestHeardAbout || undefined,
      transactionId: guestTransactionId.trim() || undefined,
    }
  }

  const handleRazorpayPayment = async (isGuest = false) => {
    setRegistering(true)
    try {
      const loaded = await loadRazorpayScript()
      if (!loaded) {
        showToast('Failed to load payment gateway. Please try again.', 'error')
        setRegistering(false)
        return
      }

      let formData = null
      if (needsExtendedForm(event)) {
        formData = { ...buildExtendedFormData(), phone: `${guestCountryCode} ${guestPhone.trim()}` }
      } else if (needsUnmaskingHalfdayApr2026Form(event)) {
        formData = buildUnmaskingHalfdayFormData()
      } else if (needsFamilyIntakeForm(event)) {
        formData = buildFamilyIntakeFormData()
      } else if (needsAWayToMoveForm(event)) {
        formData = buildAWayToMoveFormData()
      }

      const orderPayload = { eventId: event.id, formData }
      if (isGuest) {
        orderPayload.name = guestName.trim()
        orderPayload.email = guestEmail.trim()
        orderPayload.phone = guestPhone.trim()
      }

      const orderResponse = await api.post('/api/payments/create-order', orderPayload)
      const { orderId, amount, currency } = orderResponse.data.data

      const user = getUser()
      openRazorpayCheckout({
        orderId,
        amount,
        currency,
        eventName: event.name,
        prefill: {
          name: isGuest ? guestName : user?.name,
          email: isGuest ? guestEmail : user?.email,
          phone: isGuest ? guestPhone : user?.phone,
        },
        onSuccess: async (paymentData) => {
          try {
            const verifyResponse = await api.post('/api/payments/verify', paymentData)
            const registrationId = verifyResponse.data.data?.id
            trackPurchase(event, registrationId)
            trackEvent('Purchase', { transaction_id: String(registrationId), content_name: event.name, currency: 'INR' })
            trackEvent('Conversion', { 'Conversion Type': 'event_registration' })
            navigate('/events/registration-success', {
              state: { eventName: event.name, eventSlug: event.slug, isPaid: true, registrationId },
            })
          } catch (err) {
            showToast('Payment received but registration failed. Please contact support.', 'error')
          }
          setRegistering(false)
        },
        onFailure: async (failureData) => {
          await api.post('/api/payments/failure', failureData).catch(() => {})
          showToast('Payment failed. Please try again.', 'error')
          setRegistering(false)
        },
        onDismiss: () => {
          setRegistering(false)
        },
      })
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to initiate payment', 'error')
      setRegistering(false)
    }
  }

  const handleRegister = async () => {
    if (!isLoggedIn) {
      showToast('Please log in to register for events', 'info')
      return
    }
    if (needsExtendedForm(event)) {
      if (!guestPhone.trim()) {
        showToast('Please enter your phone number', 'error')
        return
      }
      if (!guestHeardAbout) {
        showToast('Please select how you heard about this workshop', 'error')
        return
      }
      if (!event.paymentGatewayEnabled && !guestTransactionId.trim()) {
        showToast('Please enter your payment transaction ID', 'error')
        return
      }
    }
    if (needsExtendedForm2(event)) {
      if (!event.paymentGatewayEnabled && !guestTransactionId.trim()) {
        showToast('Please enter your payment transaction ID', 'error')
        return
      }
    }
    if (needsUnmaskingHalfdayApr2026Form(event)) {
      if (!guestPhone.trim()) {
        showToast('Please enter your phone number', 'error')
        return
      }
      if (!validateUnmaskingHalfdayForSubmit()) return
    }
    if (needsFamilyIntakeForm(event) && !validateFamilyIntakeForSubmit()) {
      return
    }
    if (needsAWayToMoveForm(event) && !validateAWayToMoveForSubmit()) {
      return
    }
    if (event.paymentGatewayEnabled && parseFee(event.fee) > 0) {
      return handleRazorpayPayment(false)
    }
    trackAddToCart(event)
    trackInitiateCheckout(event)
    setRegistering(true)
    try {
      const body = {}
      if (needsExtendedForm(event)) {
        body.formData = {
          ...buildExtendedFormData(),
          phone: `${guestCountryCode} ${guestPhone.trim()}`,
        }
      }
      if (needsExtendedForm2(event)) {
        body.formData = { transactionId: guestTransactionId.trim() }
      }
      if (needsUnmaskingHalfdayApr2026Form(event)) {
        body.formData = buildUnmaskingHalfdayFormData()
      }
      if (needsFamilyIntakeForm(event)) {
        body.formData = buildFamilyIntakeFormData()
      }
      if (needsAWayToMoveForm(event)) {
        body.formData = buildAWayToMoveFormData()
      }
      const response = await api.post(`/api/events/${event.id}/register`, body)
      const registrationId = response.data.data?.id
      trackPurchase(event, registrationId)
      trackEvent('Purchase', {
        transaction_id: String(registrationId),
        content_name: event.name,
        currency: 'INR',
      })
      trackEvent('Conversion', { 'Conversion Type': 'event_registration' })
      navigate('/events/registration-success', {
        state: { eventName: event.name, eventSlug: event.slug, isPaid: false, registrationId },
      })
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to register', 'error')
    } finally {
      setRegistering(false)
    }
  }

  const handleGuestRegister = async (e) => {
    e.preventDefault()
    if (!guestName.trim() || !guestEmail.trim() || !guestPhone.trim()) {
      showToast('Please fill in all required fields', 'error')
      return
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(guestEmail.trim())) {
      showToast('Please enter a valid email address', 'error')
      return
    }
    if (needsExtendedForm(event) && !guestHeardAbout) {
      showToast('Please select how you heard about this workshop', 'error')
      return
    }
    if (!event.paymentGatewayEnabled && needsExtendedForm(event) && !guestTransactionId.trim()) {
      showToast('Please enter your payment transaction ID', 'error')
      return
    }
    if (!event.paymentGatewayEnabled && needsExtendedForm2(event) && !guestTransactionId.trim()) {
      showToast('Please enter your payment transaction ID', 'error')
      return
    }
    if (needsUnmaskingHalfdayApr2026Form(event) && !validateUnmaskingHalfdayForSubmit()) {
      return
    }
    if (needsFamilyIntakeForm(event) && !validateFamilyIntakeForSubmit()) {
      return
    }
    if (needsAWayToMoveForm(event) && !validateAWayToMoveForSubmit()) {
      return
    }
    if (event.paymentGatewayEnabled && parseFee(event.fee) > 0) {
      return handleRazorpayPayment(true)
    }
    trackAddToCart(event)
    trackInitiateCheckout(event)
    setRegistering(true)
    try {
      const phoneValue = needsExtendedForm(event)
        ? `${guestCountryCode} ${guestPhone.trim()}`
        : guestPhone.trim()
      const body = {
        name: guestName.trim(),
        email: guestEmail.trim(),
        phone: phoneValue,
      }
      if (needsExtendedForm(event)) {
        body.formData = buildExtendedFormData()
      }
      if (needsExtendedForm2(event)) {
        body.formData = { transactionId: guestTransactionId.trim() }
      }
      if (needsUnmaskingHalfdayApr2026Form(event)) {
        body.formData = buildUnmaskingHalfdayFormData()
      }
      if (needsFamilyIntakeForm(event)) {
        body.formData = buildFamilyIntakeFormData()
      }
      if (needsAWayToMoveForm(event)) {
        body.formData = buildAWayToMoveFormData()
      }
      const response = await api.post(`/api/events/${event.id}/guest-register`, body)
      const registrationId = response.data.data?.id
      trackPurchase(event, registrationId)
      trackEvent('Purchase', {
        transaction_id: String(registrationId),
        content_name: event.name,
        currency: 'INR',
      })
      trackEvent('Conversion', { 'Conversion Type': 'event_registration' })
      navigate('/events/registration-success', {
        state: { eventName: event.name, eventSlug: event.slug, isPaid: false, registrationId },
      })
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to register', 'error')
    } finally {
      setRegistering(false)
    }
  }

  const handleCancelRegistration = () => setConfirmModal(true)

  const confirmCancelRegistration = async () => {
    setConfirmModal(false)
    setRegistering(true)
    try {
      await api.delete(`/api/events/${event.id}/register`)
      setIsRegistered(false)
      setEvent(prev => ({ ...prev, registrationCount: Math.max((prev.registrationCount || 1) - 1, 0) }))
      showToast('Registration cancelled successfully', 'success')
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to cancel registration', 'error')
    } finally {
      setRegistering(false)
    }
  }

  const buildMediaArray = () => {
    if (!event) return []
    let allMedia = []
    if (event.image) {
      allMedia.push({ id: 'cover', type: 'image', url: event.image, isOriginalCover: true })
    }
    if (event.media?.length > 0) {
      allMedia = [...allMedia, ...event.media]
    }
    return allMedia
  }

  const renderMedia = () => {
    const allMedia = buildMediaArray()
    const currentMedia = allMedia[selectedMediaIndex] || allMedia[0]
    if (!currentMedia) {
      return (
        <img
          src={getEventImage(event)}
          alt={event.name}
          className="w-full rounded-xl object-contain max-h-[500px]"
          onError={(e) => { e.target.src = '/uploads/events/default-event.jpg' }}
        />
      )
    }

    const currentUrl = currentMedia.isOriginalCover
      ? (currentMedia.url.startsWith('http') ? currentMedia.url : `${import.meta.env.VITE_API_URL || ''}${currentMedia.url}`)
      : getMediaUrl(currentMedia.url)
    const isVideo = currentMedia.type === 'video'
    const isExternal = isVideo && isExternalVideoUrl(currentMedia.url)
    const embedUrl = isExternal ? getVideoEmbedUrl(currentMedia.url) : null

    return (
      <>
        <div className="w-full flex justify-center rounded-xl mb-3">
          {isVideo && isExternal && embedUrl ? (
            <div className="w-full aspect-video">
              <iframe
                src={embedUrl}
                className="w-full h-full rounded-xl"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Video"
              />
            </div>
          ) : isVideo && !isExternal ? (
            <video src={currentUrl} controls className="max-h-[500px] w-auto rounded-xl">
              Your browser does not support the video tag.
            </video>
          ) : (
            <img
              src={currentUrl}
              alt={event.name}
              className="max-h-[500px] w-auto rounded-xl object-contain"
              onError={(e) => { e.target.src = '/uploads/events/default-event.jpg' }}
            />
          )}
        </div>
        {allMedia.length > 1 && (
          <MediaThumbnails
            media={allMedia}
            selectedIndex={selectedMediaIndex}
            onSelect={setSelectedMediaIndex}
          />
        )}
      </>
    )
  }

  const renderRegistrationSection = () => (
    <div className="bg-primary-50 rounded-xl p-5">
      <h3 className="text-lg font-bold text-gray-900 mb-3">Registration</h3>

      {event.maxAttendees && (
        <p className="text-gray-700 mb-3">
          {event.registrationCount || 0} / {event.maxAttendees} spots filled
        </p>
      )}

      {event.registrationInfo && (
        <p className="text-gray-700 mb-4">{event.registrationInfo}</p>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        {event.registrationOpen && (
          isRegistered ? (
            <Button
              variant="secondary"
              className="flex-1"
              onClick={handleCancelRegistration}
              disabled={registering}
            >
              {registering ? 'Processing...' : '✓ Enquired - Click to Cancel'}
            </Button>
          ) : guestRegistered ? (
            <div className="flex-1 text-center py-3 bg-green-50 rounded-xl border border-green-200">
              <p className="text-green-700 font-semibold">Successfully Registered!</p>
            </div>
          ) : needsExtendedForm(event) ? (
            <form onSubmit={isLoggedIn ? (e) => { e.preventDefault(); handleRegister() } : handleGuestRegister} className="flex-1 space-y-3">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  placeholder="Your Name"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  required
                  disabled={isLoggedIn && !!currentUser?.name}
                  className={`w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isLoggedIn && currentUser?.name ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''}`}
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  placeholder="Email Address"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  required
                  disabled={isLoggedIn && !!currentUser?.email}
                  className={`w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isLoggedIn && currentUser?.email ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''}`}
                />
              </div>

              {/* Phone with Country Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number <span className="text-red-500">*</span></label>
                <div className="flex gap-2">
                  <select
                    value={guestCountryCode}
                    onChange={(e) => setGuestCountryCode(e.target.value)}
                    className="px-2 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white min-w-[90px]"
                  >
                    {COUNTRY_CODES.map(cc => (
                      <option key={cc.code} value={cc.code}>{cc.label}</option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    value={guestPhone}
                    onChange={(e) => setGuestPhone(e.target.value)}
                    required
                    className="flex-1 px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Job Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Title / Designation</label>
                <input
                  type="text"
                  placeholder="e.g. Product Manager"
                  value={guestJobTitle}
                  onChange={(e) => setGuestJobTitle(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Organization */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Organization / Company Name</label>
                <input
                  type="text"
                  placeholder="e.g. Acme Corp"
                  value={guestOrganization}
                  onChange={(e) => setGuestOrganization(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Primary Goal - Multi-select checkboxes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">What is your primary goal for attending the Breathe Right workshop?</label>
                <div className="space-y-2">
                  {PRIMARY_GOAL_OPTIONS.map((option) => (
                    <label key={option} className="flex items-start gap-2.5 cursor-pointer min-h-[44px] py-1">
                      <input
                        type="checkbox"
                        checked={guestPrimaryGoals.includes(option)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setGuestPrimaryGoals(prev => [...prev, option])
                          } else {
                            setGuestPrimaryGoals(prev => prev.filter(g => g !== option))
                          }
                        }}
                        className="mt-1 w-4 h-4 text-primary-600 rounded focus:ring-primary-500 border-gray-300 flex-shrink-0"
                      />
                      <span className="text-sm text-gray-700">{option}</span>
                    </label>
                  ))}
                  <label className="flex items-start gap-2.5 cursor-pointer min-h-[44px] py-1">
                    <input
                      type="checkbox"
                      checked={guestPrimaryGoals.includes('Other')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setGuestPrimaryGoals(prev => [...prev, 'Other'])
                        } else {
                          setGuestPrimaryGoals(prev => prev.filter(g => g !== 'Other'))
                          setGuestOtherGoal('')
                        }
                      }}
                      className="mt-1 w-4 h-4 text-primary-600 rounded focus:ring-primary-500 border-gray-300 flex-shrink-0"
                    />
                    <span className="text-sm text-gray-700">Other</span>
                  </label>
                  {guestPrimaryGoals.includes('Other') && (
                    <input
                      type="text"
                      placeholder="Please specify..."
                      value={guestOtherGoal}
                      onChange={(e) => setGuestOtherGoal(e.target.value)}
                      className="w-full ml-6 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  )}
                </div>
              </div>

              {/* How did you hear about us - Single-select radio */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">How did you hear about this workshop? <span className="text-red-500">*</span></label>
                <div className="space-y-2">
                  {HEARD_ABOUT_OPTIONS.map((option) => (
                    <label key={option} className="flex items-center gap-2.5 cursor-pointer min-h-[44px] py-1">
                      <input
                        type="radio"
                        name="heardAboutDetail"
                        value={option}
                        checked={guestHeardAbout === option}
                        onChange={(e) => { setGuestHeardAbout(e.target.value); setGuestOtherHeardAbout('') }}
                        className="w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300 flex-shrink-0"
                      />
                      <span className="text-sm text-gray-700">{option}</span>
                    </label>
                  ))}
                  <label className="flex items-center gap-2.5 cursor-pointer min-h-[44px] py-1">
                    <input
                      type="radio"
                      name="heardAboutDetail"
                      value="Other"
                      checked={guestHeardAbout === 'Other'}
                      onChange={(e) => setGuestHeardAbout(e.target.value)}
                      className="w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300 flex-shrink-0"
                    />
                    <span className="text-sm text-gray-700">Other</span>
                  </label>
                  {guestHeardAbout === 'Other' && (
                    <input
                      type="text"
                      placeholder="Please specify..."
                      value={guestOtherHeardAbout}
                      onChange={(e) => setGuestOtherHeardAbout(e.target.value)}
                      className="w-full ml-6 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  )}
                </div>
              </div>

              {!event.paymentGatewayEnabled && (
                <>
                  <div className="rounded-xl border border-primary-200 bg-white p-3">
                    <p className="text-sm font-medium text-gray-800 mb-2">
                      {EXTENDED_FORM_PAYMENT_TEXT}
                    </p>
                    <img
                      src="/payment-qr-code.png"
                      alt="Payment QR code for Rs. 99 transfer"
                      className="w-full max-w-[260px] mx-auto rounded-lg border border-gray-200"
                      loading="lazy"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Transaction ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter your UPI transaction ID"
                      value={guestTransactionId}
                      onChange={(e) => setGuestTransactionId(e.target.value)}
                      required
                      className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </>
              )}
              {event.paymentGatewayEnabled && (
                <div className="rounded-xl border border-green-200 bg-green-50 p-3">
                  <p className="text-sm font-medium text-green-800">Payment will be collected securely via Razorpay checkout after you submit.</p>
                </div>
              )}

              <Button
                type="submit"
                variant="primary"
                className="w-full"
                disabled={registering || (event.maxAttendees && event.registrationCount >= event.maxAttendees)}
              >
                {registering ? 'Processing...' :
                  (event.maxAttendees && event.registrationCount >= event.maxAttendees)
                    ? 'Event Full'
                    : 'Register Now'}
              </Button>
            </form>
          ) : needsExtendedForm2(event) ? (
            <form onSubmit={isLoggedIn ? (e) => { e.preventDefault(); handleRegister() } : handleGuestRegister} className="flex-1 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  placeholder="Your Name"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  required
                  disabled={isLoggedIn && !!currentUser?.name}
                  className={`w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isLoggedIn && currentUser?.name ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''}`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  placeholder="Email Address"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  required
                  disabled={isLoggedIn && !!currentUser?.email}
                  className={`w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isLoggedIn && currentUser?.email ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''}`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number <span className="text-red-500">*</span></label>
                <input
                  type="tel"
                  placeholder="Phone Number"
                  value={guestPhone}
                  onChange={(e) => setGuestPhone(e.target.value)}
                  required
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              {!event.paymentGatewayEnabled ? (
                <ExtendedForm2PaymentSection
                  fee={event.fee}
                  transactionId={guestTransactionId}
                  onTransactionIdChange={setGuestTransactionId}
                  disabled={registering}
                />
              ) : (
                <div className="rounded-xl border border-green-200 bg-green-50 p-3">
                  <p className="text-sm font-medium text-green-800">Payment will be collected securely via Razorpay checkout after you submit.</p>
                </div>
              )}
              <Button
                type="submit"
                variant="primary"
                className="w-full"
                disabled={registering || (event.maxAttendees && event.registrationCount >= event.maxAttendees)}
              >
                {registering ? 'Processing...' :
                  (event.maxAttendees && event.registrationCount >= event.maxAttendees)
                    ? 'Event Full'
                    : 'Register Now'}
              </Button>
            </form>
          ) : needsFamilyIntakeForm(event) ? (
            <form onSubmit={isLoggedIn ? (e) => { e.preventDefault(); handleRegister() } : handleGuestRegister} className="flex-1 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  placeholder="Your full name"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  required
                  disabled={isLoggedIn && !!currentUser?.name}
                  className={`w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isLoggedIn && currentUser?.name ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''}`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone number (WhatsApp preferred) <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  placeholder="Phone / WhatsApp"
                  value={guestPhone}
                  onChange={(e) => setGuestPhone(e.target.value)}
                  required
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email address <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  placeholder="Email address"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  required
                  disabled={isLoggedIn && !!currentUser?.email}
                  className={`w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isLoggedIn && currentUser?.email ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''}`}
                />
              </div>
              <div>
                <p className="block text-sm font-medium text-gray-700 mb-2">Your child&apos;s gender <span className="text-red-500">*</span></p>
                <div className="space-y-2">
                  <label className="flex items-center gap-2.5 cursor-pointer min-h-[44px] py-1">
                    <input
                      type="radio"
                      name="familyIntakeChildGenderDetail"
                      value="Male"
                      checked={guestChildGender === 'Male'}
                      onChange={() => { setGuestChildGender('Male'); setGuestChildGenderOther('') }}
                      className="w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300 flex-shrink-0"
                    />
                    <span className="text-sm text-gray-700">Male</span>
                  </label>
                  <label className="flex items-center gap-2.5 cursor-pointer min-h-[44px] py-1">
                    <input
                      type="radio"
                      name="familyIntakeChildGenderDetail"
                      value="Other"
                      checked={guestChildGender === 'Other'}
                      onChange={() => setGuestChildGender('Other')}
                      className="w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300 flex-shrink-0"
                    />
                    <span className="text-sm text-gray-700">Other</span>
                  </label>
                  {guestChildGender === 'Other' && (
                    <input
                      type="text"
                      placeholder="Please specify"
                      value={guestChildGenderOther}
                      onChange={(e) => setGuestChildGenderOther(e.target.value)}
                      className="w-full sm:ml-6 px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your child&apos;s age <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="Age"
                  value={guestChildAge}
                  onChange={(e) => setGuestChildAge(e.target.value)}
                  required
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Can you tell us a little about yourself. Please feel free to share as little and as much as you like. <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={4}
                  value={guestAboutYourself}
                  onChange={(e) => setGuestAboutYourself(e.target.value)}
                  required
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y min-h-[100px]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">What do you hope to get out of this session? <span className="text-red-500">*</span></label>
                <textarea
                  rows={3}
                  value={guestSessionHope}
                  onChange={(e) => setGuestSessionHope(e.target.value)}
                  required
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y min-h-[88px]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">If you have any questions, you can share them here. <span className="text-red-500">*</span></label>
                <textarea
                  rows={3}
                  value={guestSharedQuestions}
                  onChange={(e) => setGuestSharedQuestions(e.target.value)}
                  required
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y min-h-[88px]"
                />
              </div>
              <Button
                type="submit"
                variant="primary"
                className="w-full"
                disabled={registering || (event.maxAttendees && event.registrationCount >= event.maxAttendees)}
              >
                {registering ? 'Processing...' :
                  (event.maxAttendees && event.registrationCount >= event.maxAttendees)
                    ? 'Event Full'
                    : 'Register Now'}
              </Button>
            </form>
          ) : needsUnmaskingHalfdayApr2026Form(event) ? (
            <form onSubmit={isLoggedIn ? (e) => { e.preventDefault(); handleRegister() } : handleGuestRegister} className="flex-1 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  placeholder="Your name"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  required
                  disabled={isLoggedIn && !!currentUser?.name}
                  className={`w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isLoggedIn && currentUser?.name ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''}`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  placeholder="Email"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  required
                  disabled={isLoggedIn && !!currentUser?.email}
                  className={`w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isLoggedIn && currentUser?.email ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''}`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone <span className="text-red-500">*</span></label>
                <input
                  type="tel"
                  placeholder="Phone"
                  value={guestPhone}
                  onChange={(e) => setGuestPhone(e.target.value)}
                  required
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Age <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="Age"
                  value={guestAge}
                  onChange={(e) => setGuestAge(e.target.value)}
                  required
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gender <span className="text-red-500">*</span></label>
                <select
                  value={guestGender}
                  onChange={(e) => { setGuestGender(e.target.value); if (e.target.value !== 'Other') setGuestGenderOther('') }}
                  required
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
                >
                  <option value="">Select…</option>
                  {UNMASKING_GENDER_OPTIONS.map((g) => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
                {guestGender === 'Other' && (
                  <input
                    type="text"
                    placeholder="Please specify"
                    value={guestGenderOther}
                    onChange={(e) => setGuestGenderOther(e.target.value)}
                    className="w-full mt-2 px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  placeholder="Company or organization"
                  value={guestCompany}
                  onChange={(e) => setGuestCompany(e.target.value)}
                  required
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <p className="block text-sm font-medium text-gray-700 mb-2">Session selection <span className="text-red-500">*</span></p>
                <div className="space-y-2">
                  {UNMASKING_SESSION_OPTIONS.map((opt) => (
                    <label key={opt.value} className="flex items-start gap-2.5 cursor-pointer min-h-[44px] py-1">
                      <input
                        type="radio"
                        name="unmaskingSessionDetail"
                        value={opt.value}
                        checked={guestSessionSelection === opt.value}
                        onChange={() => setGuestSessionSelection(opt.value)}
                        className="mt-1 w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300 flex-shrink-0"
                      />
                      <span className="text-sm text-gray-700">
                        <span className="font-medium">{opt.value}</span>
                        <span className="block text-gray-500 text-xs mt-0.5">{opt.subtitle}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              {!event.paymentGatewayEnabled ? (
                <ExtendedForm2PaymentSection
                  fee={event.fee}
                  transactionId={guestTransactionId}
                  onTransactionIdChange={setGuestTransactionId}
                  disabled={registering}
                />
              ) : (
                <div className="rounded-xl border border-green-200 bg-green-50 p-3">
                  <p className="text-sm font-medium text-green-800">Payment will be collected securely via Razorpay checkout after you submit.</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Questions for facilitators or organizers</label>
                <textarea
                  rows={3}
                  placeholder="Optional"
                  value={guestFacilitatorQueries}
                  onChange={(e) => setGuestFacilitatorQueries(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y min-h-[88px]"
                />
              </div>
              <Button
                type="submit"
                variant="primary"
                className="w-full"
                disabled={registering || (event.maxAttendees && event.registrationCount >= event.maxAttendees)}
              >
                {registering ? 'Processing...' :
                  (event.maxAttendees && event.registrationCount >= event.maxAttendees)
                    ? 'Event Full'
                    : 'Register Now'}
              </Button>
            </form>
          ) : needsAWayToMoveForm(event) ? (
            <form onSubmit={isLoggedIn ? (e) => { e.preventDefault(); handleRegister() } : handleGuestRegister} className="flex-1 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  placeholder="Your name"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  required
                  disabled={isLoggedIn && !!currentUser?.name}
                  className={`w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isLoggedIn && currentUser?.name ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''}`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  placeholder="Email"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  required
                  disabled={isLoggedIn && !!currentUser?.email}
                  className={`w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isLoggedIn && currentUser?.email ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''}`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone <span className="text-red-500">*</span></label>
                <input
                  type="tel"
                  placeholder="Phone"
                  value={guestPhone}
                  onChange={(e) => setGuestPhone(e.target.value)}
                  required
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <AWayToMoveSlotPicker
                name="aWayToMoveSlotDetail"
                selectedSlot={guestSlotSelection}
                onChange={setGuestSlotSelection}
                disabled={registering}
              />
              <Button
                type="submit"
                variant="primary"
                className="w-full"
                disabled={registering || getAvailableAWayToMoveSlotOptions().length === 0}
              >
                {registering ? 'Processing...' : 'Enquire Now'}
              </Button>
            </form>
          ) : isLoggedIn ? (
            <Button
              variant="primary"
              className="flex-1"
              onClick={handleRegister}
              disabled={registering || (event.maxAttendees && event.registrationCount >= event.maxAttendees)}
            >
              {registering ? 'Processing...' :
                (event.maxAttendees && event.registrationCount >= event.maxAttendees)
                  ? 'Event Full'
                  : 'Enquire Now'}
            </Button>
          ) : (
            <form onSubmit={handleGuestRegister} className="flex-1 space-y-2.5">
              <input
                type="text"
                placeholder="Your Name"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                required
                className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <input
                type="email"
                placeholder="Email Address"
                value={guestEmail}
                onChange={(e) => setGuestEmail(e.target.value)}
                required
                className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <input
                type="tel"
                placeholder="Phone Number"
                value={guestPhone}
                onChange={(e) => setGuestPhone(e.target.value)}
                required
                className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <Button
                type="submit"
                variant="primary"
                className="w-full"
                disabled={registering || (event.maxAttendees && event.registrationCount >= event.maxAttendees)}
              >
                {registering ? 'Processing...' :
                  (event.maxAttendees && event.registrationCount >= event.maxAttendees)
                    ? 'Event Full'
                    : 'Enquire Now'}
              </Button>
            </form>
          )
        )}

        {event.registrationLink && (
          <a
            href={event.registrationLink}
            target={event.registrationLink.startsWith('tel:') ? '_self' : '_blank'}
            rel="noopener noreferrer"
            className="flex-1"
          >
            <Button variant="outline" className="w-full">
              External Registration →
            </Button>
          </a>
        )}
      </div>
    </div>
  )

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-cream to-primary-100 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-cream to-primary-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8 text-center max-w-md shadow-lg">
          <div className="text-4xl mb-4">⚠️</div>
          <p className="text-gray-600 mb-4">{error}</p>
          <Link to="/events">
            <Button variant="primary">Back to All Events</Button>
          </Link>
        </div>
      </div>
    )
  }

  if (!event) return null

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-cream to-primary-100">
      <AnimatePresence>
        {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
      </AnimatePresence>

      <AnimatePresence>
        <ConfirmModal
          isOpen={confirmModal}
          title="Cancel Registration"
          message="Are you sure you want to cancel your registration for this event?"
          confirmText="Yes, Cancel"
          cancelText="Keep Registration"
          onConfirm={confirmCancelRegistration}
          onCancel={() => setConfirmModal(false)}
        />
      </AnimatePresence>

      {/* Back Link */}
      <div className="container mx-auto px-4 pt-4 pb-2">
        <Link
          to="/events"
          className="inline-flex items-center gap-1.5 text-primary-600 hover:text-primary-700 font-medium text-sm transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to All Events
        </Link>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 pb-12">
        <div className="md:flex md:gap-8 md:items-start">

          {/* Left Column: Media + Descriptions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="md:flex-1 md:min-w-0"
          >
            {/* Media Gallery */}
            <div className="mb-6">
              {renderMedia()}
            </div>

            {/* Event Name + Badges (mobile only, shown above sidebar) */}
            <div className="md:hidden mb-6">
              <div className="mb-3 flex flex-wrap gap-2">
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getFormatBadgeColor(event.format)}`}>
                  {event.format}
                </span>
                {event.fee?.toLowerCase() === 'free' && (
                  <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                    FREE
                  </span>
                )}
                {event.registrationOpen && event.maxAttendees && (
                  <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                    {event.registrationCount || 0}/{event.maxAttendees} spots
                  </span>
                )}
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-1">{event.name}</h1>
              {event.fee && event.fee.toLowerCase() !== 'free' && (
                <p className="text-xl font-bold text-primary-600">{event.fee}</p>
              )}
            </div>

            {/* Info Block (mobile only) */}
            <div className="md:hidden mb-6 space-y-3">
              <div className="flex items-start gap-3">
                <span className="text-xl">📅</span>
                <div>
                  <p className="font-semibold text-gray-900">{formatDateRange(event.eventDate, event.endDate)}</p>
                  <p className="text-gray-600 text-sm">
                    {formatTime(event.eventDate)}
                    {event.endDate && ` - ${formatTime(event.endDate)}`}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <span className="text-xl">📍</span>
                <div>
                  <p className="text-gray-900">{getLocationText(event)}</p>
                  {(event.venueLink || event.space?.googleMapsUrl) && (
                    <a
                      href={event.venueLink || event.space?.googleMapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:underline text-sm"
                    >
                      View on Map →
                    </a>
                  )}
                </div>
              </div>

              {getFacilitatorNames(event).length > 0 && (
                <div className="flex items-start gap-3">
                  <span className="text-xl">👤</span>
                  <div>
                    <p className="font-semibold text-gray-900">Facilitators</p>
                    <p className="text-gray-600">{renderFacilitatorLinks(event)}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Registration (mobile only) */}
            <div className="md:hidden mb-6">
              {renderRegistrationSection()}
            </div>

            {/* About This Event */}
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-3">About This Event</h2>
              <p className="text-gray-700 leading-relaxed font-semibold">
                {event.shortDescription}
              </p>
            </div>

            {/* Detailed Description */}
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-3">Details</h2>
              <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                {event.detailedDescription}
              </div>
            </div>

            {/* Post-Event Resources */}
            {event.postEventResources?.length > 0 && (
              <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Post-Event Resources</h2>
                <div className="space-y-3">
                  {event.postEventResources.map((resource) => {
                    const isVideo = resource.resourceType === 'recording'
                    const isExternal = resource.type === 'link'
                    const embedUrl = isVideo && isExternal ? getVideoEmbedUrl(resource.url) : null
                    const resourceUrl = isExternal ? resource.url : getMediaUrl(resource.url)

                    return (
                      <div key={resource.id} className="rounded-xl border border-gray-200 overflow-hidden bg-white">
                        {embedUrl && (
                          <div className="aspect-video w-full">
                            <iframe
                              src={embedUrl}
                              className="w-full h-full"
                              allowFullScreen
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              title={resource.title}
                            />
                          </div>
                        )}
                        <a
                          href={resourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors group"
                        >
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            resource.resourceType === 'recording' ? 'bg-red-100 text-red-600' :
                            resource.resourceType === 'pdf' ? 'bg-orange-100 text-orange-600' :
                            resource.resourceType === 'document' ? 'bg-blue-100 text-blue-600' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {resource.resourceType === 'recording' && (
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                            )}
                            {resource.resourceType === 'pdf' && (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                            )}
                            {resource.resourceType === 'document' && (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                            )}
                            {resource.resourceType === 'other' && (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors truncate">
                              {resource.title}
                            </p>
                            {resource.description && (
                              <p className="text-sm text-gray-500 line-clamp-1">{resource.description}</p>
                            )}
                          </div>
                          <svg className="w-5 h-5 text-gray-400 group-hover:text-primary-600 flex-shrink-0 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

          </motion.div>

          {/* Right Column: Sticky Sidebar (desktop only) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="hidden md:block md:w-[380px] md:flex-shrink-0"
          >
            <div className="sticky top-24">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                {/* Sidebar Header */}
                <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white p-5">
                  <div className="mb-3 flex flex-wrap gap-2">
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-white/20">
                      {event.format}
                    </span>
                    {event.fee?.toLowerCase() === 'free' && (
                      <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-yellow-400 text-yellow-900">
                        FREE
                      </span>
                    )}
                  </div>
                  <h1 className="text-2xl font-bold mb-1">{event.name}</h1>
                  {event.fee && event.fee.toLowerCase() !== 'free' && (
                    <p className="text-xl font-bold">{event.fee}</p>
                  )}
                </div>

                {/* Sidebar Body */}
                <div className="p-5 space-y-4">
                  <div className="flex items-start gap-3">
                    <span className="text-xl">📅</span>
                    <div>
                      <p className="font-semibold text-gray-900">{formatDateRange(event.eventDate, event.endDate)}</p>
                      <p className="text-gray-600 text-sm">
                        {formatTime(event.eventDate)}
                        {event.endDate && ` - ${formatTime(event.endDate)}`}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <span className="text-xl">📍</span>
                    <div>
                      <p className="text-gray-900">{getLocationText(event)}</p>
                      {(event.venueLink || event.space?.googleMapsUrl) && (
                        <a
                          href={event.venueLink || event.space?.googleMapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:underline text-sm"
                        >
                          View on Map →
                        </a>
                      )}
                    </div>
                  </div>

                  {getFacilitatorNames(event).length > 0 && (
                    <div className="flex items-start gap-3">
                      <span className="text-xl">👤</span>
                      <div>
                        <p className="font-semibold text-gray-900">Facilitators</p>
                        <p className="text-gray-600">{renderFacilitatorLinks(event)}</p>
                      </div>
                    </div>
                  )}

                  <hr className="border-gray-200" />

                  {renderRegistrationSection()}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default EventDetail
